
import React, { useState, useEffect } from 'react';
import { Star, MessageSquare, Save, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { supabase } from '../services/supabaseService';
import { FeedbackScorecard as IScorecard, CandidateFeedback } from '../types';

interface FeedbackScorecardProps {
    interviewId: string;
    stageId: string;
    candidateName: string;
    onSubmitted: () => void;
    /** When true, criteria and submit are disabled (e.g. client portal). */
    readOnly?: boolean;
}

const FeedbackScorecard: React.FC<FeedbackScorecardProps> = ({ interviewId, stageId, candidateName, onSubmitted, readOnly = false }) => {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [scorecard, setScorecard] = useState<IScorecard | null>(null);
    const [scores, setScores] = useState<Record<string, number>>({});
    const [notes, setNotes] = useState('');
    const [recommendation, setRecommendation] = useState<CandidateFeedback['recommendation']>('Maybe');

    useEffect(() => {
        fetchScorecard();
    }, [stageId]);

    const fetchScorecard = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('feedback_forms')
                .select('*')
                .eq('stage_id', stageId)
                .single();

            if (data) {
                setScorecard(data as IScorecard);
                // Init scores
                const initialScores: Record<string, number> = {};
                data.criteria.forEach((c: any) => initialScores[c.id] = 0);
                setScores(initialScores);
            } else {
                // No scorecard defined for this stage
                setScorecard(null);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (readOnly) return;
        setSubmitting(true);
        try {
            // Calculate overall score (simple average for now)
            const values = Object.values(scores);
            const overall = values.length > 0 ? values.reduce((a: number, b: number) => a + b, 0) / values.length : 0;

            const { error } = await supabase.from('candidate_feedback').insert({
                interview_id: interviewId,
                reviewer_id: (await supabase.auth.getUser()).data.user?.id,
                scores,
                overall_score: Math.round(overall * 10) / 10,
                notes,
                recommendation
            });

            if (error) throw error;
            onSubmitted();
        } catch (e) {
            console.error(e);
            alert("Kon feedback niet opslaan.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-slate-300" /></div>;

    if (!scorecard) {
        return (
            <div className="text-center p-6 bg-slate-50 rounded-xl border border-slate-200">
                <AlertCircle className="mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-500">Geen scorecard ingesteld voor deze fase.</p>
                <p className="text-xs text-slate-400 mt-1">Configureer dit in de Interview Settings.</p>
            </div>
        );
    }

    return (
        <div className={`bg-white p-6 rounded-2xl border border-slate-200 shadow-sm ${readOnly ? 'opacity-95' : ''}`}>
            <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight mb-4">Feedback: {candidateName}</h3>
            {readOnly && (
                <p className="text-xs text-slate-500 mb-4 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                    In het clientportaal kun je geen interviewfeedback invullen of opslaan.
                </p>
            )}

            <div className="space-y-6 mb-8">
                {scorecard.criteria.map((criterion) => (
                    <div key={criterion.id} className="border-b border-slate-100 pb-4 last:border-0 last:pb-0">
                        <div className="flex justify-between items-start mb-2">
                            <div>
                                <h4 className="font-bold text-slate-700 text-sm">{criterion.category}</h4>
                                <ul className="text-xs text-slate-500 list-disc list-inside mt-1">
                                    {criterion.questions.map((q, i) => <li key={i}>{q}</li>)}
                                </ul>
                            </div>
                            <div className={`flex gap-1 ${readOnly ? 'pointer-events-none opacity-50' : ''}`}>
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                        type="button"
                                        key={star}
                                        disabled={readOnly}
                                        onClick={() => !readOnly && setScores({ ...scores, [criterion.id]: star })}
                                        className={`p-1 rounded-full transition-all ${scores[criterion.id] >= star ? 'text-amber-400 scale-110' : 'text-slate-200 hover:text-amber-200'}`}
                                    >
                                        <Star size={20} fill={scores[criterion.id] >= star ? "currentColor" : "none"} />
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="space-y-4">
                <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Notities</label>
                    <textarea
                        value={notes}
                        readOnly={readOnly}
                        onChange={e => !readOnly && setNotes(e.target.value)}
                        className={`w-full h-24 p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-red- ${readOnly ? 'cursor-not-allowed text-slate-500' : ''}`}
                        placeholder="Kwalitatieve feedback..."
                    />
                </div>

                <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Advies</label>
                    <div className={`grid grid-cols-4 gap-2 ${readOnly ? 'pointer-events-none opacity-60' : ''}`}>
                        {(['No Hire', 'Maybe', 'Hire', 'Strong Hire'] as const).map(rec => (
                            <button
                                type="button"
                                key={rec}
                                disabled={readOnly}
                                onClick={() => !readOnly && setRecommendation(rec)}
                                className={`py-3 rounded-xl text-xs font-bold transition-all border ${recommendation === rec
                                    ? rec === 'Strong Hire' ? 'bg-emerald-600 text-white border-emerald-600'
                                        : rec === 'Hire' ? 'bg-green-500 text-white border-green-500'
                                            : rec === 'Maybe' ? 'bg-amber-400 text-white border-amber-400'
                                                : 'bg-red-500 text-white border-red-500'
                                    : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'
                                    }`}
                            >
                                {rec}
                            </button>
                        ))}
                    </div>
                </div>

                {!readOnly && (
                <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="w-full py-4 bg-slate-900 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-black transition-all shadow-lg flex items-center justify-center gap-2 mt-4"
                >
                    {submitting ? <Loader2 className="animate-spin" /> : <Save size={16} />} Feedback Opslaan
                </button>
                )}
            </div>
        </div>
    );
};

export default FeedbackScorecard;
